import { OutlinedButton, PageContainer, RoundedButton } from "@greeter/matter";
import { FetchBundle } from "@greeter/venue-api";
import {
  IonContent,
  IonPage,
  useIonRouter,
  useIonViewDidEnter,
} from "@ionic/react";
import { useQuery } from "@tanstack/react-query";
import {
  Bundle,
  getDiscountedPrice,
  getSavings,
  hasDiscounts,
  ImageAsset,
  toDiscountable,
} from "@greeter/core";
import { classNames } from "@greeter/util";

import css from "./EditBundleCompletedPage.module.scss";
import { useHistory } from "react-router-dom";
import { Routes } from "@greeter-venue/utility/Routes";

const priceFormatter = Intl.NumberFormat("da-DK", { maximumFractionDigits: 0 });

export type BundleDisplayProps = {
  bundle: Bundle;
};
function BundleDisplay(props: BundleDisplayProps) {
  return (
    <div className={css.BundleDisplay}>
      <div className={css.BundleDisplayHeader}>
        <img
          {...classNames(
            "cover",
            "w-full",
            "h-full",
            "aspect-ratio-16-6",
            css.BundleDisplayCover
          )}
          src={
            props.bundle.coverAsset
              ? ImageAsset.findUriWithSizeOrDefault(
                  props.bundle.coverAsset,
                  "16x9-w512"
                )
              : props.bundle.coverUrl ?? ""
          }
        />
        {hasDiscounts(props.bundle) && (
          <div className={css.BundleLabel}>
            Spar {priceFormatter.format(getSavings(props.bundle))} kr.
          </div>
        )}
      </div>
      <div className={css.BundleDisplayBody}>
        <div className={css.BundleDisplayTitle}>{props.bundle.title}</div>
        {props.bundle.lines.slice(0, 3).map((l) => {
          return (
            <div className={css.BundleDisplayLine}>
              {l.quantity} x {l.name}
            </div>
          );
        })}
        {props.bundle.lines.length > 3 && (
          <div className={css.BundleDisplayLine}>...</div>
        )}
        <div className={css.BundleDisplayPrice}>
          {hasDiscounts(props.bundle) && (
            <span>
              {getDiscountedPrice(toDiscountable(props.bundle))}{" "}
              <s>{props.bundle.price}</s>
            </span>
          )}{" "}
          kr.
        </div>
      </div>
    </div>
  );
}

export interface EditBundleCompletedPageApiRequirements {
  fetchBundle: FetchBundle;
}

type EditBundleCompletedPageParams = {
  id: string;
};

export type EditBundleCompletedPageProps = {
  api: EditBundleCompletedPageApiRequirements;
  bundleId: string;
};

export function EditBundleCompletedPage(props: EditBundleCompletedPageProps) {
  const router = useIonRouter();
  const bundleQuery = useQuery({
    queryKey: ["bundle", props.bundleId],
    queryFn: () => props.api.fetchBundle(props.bundleId),
  });

  useIonViewDidEnter(() => {
    bundleQuery.refetch();
  }, []);

  return (
    <IonPage>
      <IonContent>
        <PageContainer>
          <div className={css.EditBundleCompletedPage}>
            <h1>Bordpakke gemt</h1>
            {bundleQuery.isLoading ? (
              <div className="aspect-ratio-4-3 skeleton shine w-full border-radius-md" />
            ) : bundleQuery.isError ? (
              <div
                className="aspect-ratio-4-3 w-full error-outline border-radius-md grid place-center"
                onClick={() => bundleQuery.refetch()}
              >
                Kunne ikke hente bordpakke. <b>Tryk for at prøve igen.</b>
              </div>
            ) : (
              <BundleDisplay bundle={bundleQuery.data!} />
            )}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
                alignSelf: "end",
              }}
            >
              <RoundedButton
                style={{
                  backgroundColor: "var(--gm-color-secondary-highlight)",
                  fontWeight: "bold",
                  fontSize: "1.25rem",
                  padding: "1rem 0",
                }}
                onClick={() =>
                  router.push(Routes.mainGroup.venueTab.editBundle.route({}))
                }
              >
                OPRET EN MERE
              </RoundedButton>
              <OutlinedButton
                data-testid="btn-finish-bundle-editing"
                style={{ fontSize: "1.25rem", fontWeight: "bold" }}
                onClick={() =>
                  router.push(
                    Routes.mainGroup.venueTab.root.route(),
                    "forward",
                    "push",
                    {
                      unmount: true,
                    }
                  )
                }
              >
                AFSLUT
              </OutlinedButton>
            </div>
          </div>
        </PageContainer>
      </IonContent>
    </IonPage>
  );
}
